import { useCallback, useState } from 'react';
import { QueryParamConfig, useQueryParam } from 'use-query-params';

/**
 * Hook to manage a state that can be optionally synced with a URL query parameter.
 * Works similarly to `useQueryParam` from `use-query-params`, but allows for optional local state management as well.
 */
export function useOptionalQueryParamState<T>(args: {
  queryParamName: string;
  initialValue: T;
  enableQueryParam?: boolean;
  paramConfig: QueryParamConfig<T>;
}): [T, (v: T) => void] {
  const enableQueryParam = args.enableQueryParam ?? false;
  const [rawURLState, setURLState] = useQueryParam(args.queryParamName, args.paramConfig);
  let urlState = rawURLState;
  if (urlState === null || urlState === undefined) {
    urlState = args.initialValue;
  }
  const [localState, setLocalState] = useState<T>(args.initialValue);
  const setState = useCallback(
    (v: T) => {
      // If enableQueryParam is true, set the URL state to the value passed in
      // Otherwise, set the local state to the value passed in
      if (enableQueryParam) {
        setURLState(v);
      } else {
        setLocalState(v);
      }
    },
    [enableQueryParam, setURLState, setLocalState]
  );
  // If enableQueryParam is true, return the URL state as the source of truth
  const state = enableQueryParam ? urlState : localState;
  return [state as T, setState];
}
